import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

/* =========================================================
   API CONFIG
========================================================= */

const API_BASE_URL = "https://coinyoco.world/brobizee-api";
const API_USER_PROFILE_URL = `${API_BASE_URL}/get_user_profile.php`;

const DEFAULT_AVATAR =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=300&q=80";

/* =========================================================
   HELPERS
========================================================= */

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  return String(value).trim() || fallback;
};

const getProfileImage = (profile, baseUrl) => {
  const image = safeString(profile, "");
  if (!image) return DEFAULT_AVATAR;

  if (
    image.startsWith("http://") ||
    image.startsWith("https://") ||
    image.startsWith("data:image/")
  ) {
    return image;
  }

  if (image.startsWith("/")) {
    return `${baseUrl}${image}`;
  }

  return `${baseUrl}/${image}`;
};

const formatDate = (value) => {
  const raw = safeString(value, "");
  if (!raw) return "—";

  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;

    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return raw;
  }
};

/* =========================================================
   COMPONENT
========================================================= */

export default function UserProfilePopup({
  userId,
  deviceUuid,
  onClose,
}) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* =====================================================
     FETCH USER
  ===================================================== */

  const fetchUser = useCallback(async () => {
    if (!userId || !deviceUuid) {
      throw new Error("User ID or Device UUID missing.");
    }

    const url =
      `${API_USER_PROFILE_URL}` +
      `?id=${encodeURIComponent(userId)}` +
      `&device_uuid=${encodeURIComponent(deviceUuid)}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error("Invalid JSON from server.");
    }

    if (!result?.success || !result?.data) {
      throw new Error(result?.message || "User not found.");
    }

    return result.data;
  }, [userId, deviceUuid]);

  /* =====================================================
     LOAD
  ===================================================== */

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await fetchUser();

        if (cancelled) return;
        setUser(data);
      } catch (err) {
        console.error("Fetch user error:", err);
        if (cancelled) return;
        setError(err?.message || "Unable to load user.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchUser]);

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10001,
        background: "rgba(8, 18, 30, 0.72)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "14px",
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "440px",
          maxHeight: "92vh",
          overflow: "hidden",
          borderRadius: "28px",
          background: "linear-gradient(180deg,#ffffff,#f5f8fc)",
          boxShadow: "0 30px 90px rgba(0,0,0,.35)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            height: "5px",
            background:
              "linear-gradient(90deg,#0066cc,#00a8ff,#ff6a00)",
          }}
        />

        <div
          style={{
            padding: "17px 17px 14px",
            borderBottom: "1px solid #e9eef3",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "11px",
            }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "15px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg,#0066cc,#008cff)",
                color: "#fff",
                fontSize: "20px",
              }}
            >
              👤
            </div>

            <div>
              <div
                style={{
                  fontSize: "9px",
                  fontWeight: 900,
                  letterSpacing: "1.2px",
                  color: "#0066cc",
                }}
              >
                USER PROFILE
              </div>

              <h2
                style={{
                  margin: "3px 0 0",
                  fontSize: "18px",
                  color: "#17212b",
                  fontWeight: 900,
                }}
              >
                User Details
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "50%",
              border: "1px solid #e0e7ee",
              background: "#fff",
              cursor: "pointer",
              fontSize: "20px",
              color: "#52606d",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "16px 15px 20px",
          }}
        >
          {loading && (
            <div
              style={{
                padding: "50px 20px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "50%",
                  border: "4px solid #e6eef6",
                  borderTopColor: "#0066cc",
                  animation:
                    "brobizeeUserSpin 0.9s linear infinite",
                }}
              />

              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "#52606d",
                  fontWeight: 600,
                }}
              >
                Loading user details...
              </p>

              <style>{`
                @keyframes brobizeeUserSpin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}

          {!loading && error && (
            <div
              style={{
                padding: "30px 20px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <div style={{ fontSize: "42px" }}>⚠️</div>

              <h3 style={{ margin: 0, fontSize: "15px" }}>
                Unable to Load Profile
              </h3>

              <p
                style={{
                  margin: 0,
                  fontSize: "11px",
                  color: "#707b86",
                }}
              >
                {error}
              </p>
            </div>
          )}

          {!loading && !error && user && (
            <>
              <div
                style={{
                  padding: "22px 16px 18px",
                  borderRadius: "22px",
                  background:
                    "linear-gradient(145deg,#ffffff,#eef6ff)",
                  border: "1px solid #e3ebf2",
                  textAlign: "center",
                  marginBottom: "14px",
                }}
              >
                <img
                  src={getProfileImage(
                    user.profile_user_url || user.profile_user,
                    API_BASE_URL
                  )}
                  alt={user.name || "User"}
                  style={{
                    width: "88px",
                    height: "88px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid #ffffff",
                    boxShadow: "0 10px 26px rgba(0,92,169,.18)",
                    marginBottom: "12px",
                  }}
                  onError={(e) => {
                    e.currentTarget.src = DEFAULT_AVATAR;
                  }}
                />

                <h2
                  style={{
                    margin: "0 0 8px",
                    fontSize: "20px",
                    fontWeight: 900,
                    color: "#0f172a",
                  }}
                >
                  {safeString(user.name, "BroBizee User")}
                </h2>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "6px",
                    marginBottom: "10px",
                  }}
                >
                  <span
                    style={{
                      padding: "5px 10px",
                      borderRadius: "999px",
                      background: "rgba(0,92,169,.08)",
                      color: "#005CA9",
                      fontSize: "10.5px",
                      fontWeight: 700,
                    }}
                  >
                    📞 {safeString(user.mobile_number, "—")}
                  </span>

                  <span
                    style={{
                      padding: "5px 10px",
                      borderRadius: "999px",
                      background: "rgba(0,92,169,.08)",
                      color: "#005CA9",
                      fontSize: "10.5px",
                      fontWeight: 700,
                    }}
                  >
                    ✉️ {safeString(user.email, "—")}
                  </span>
                </div>

                <div
                  style={{
                    display: "inline-block",
                    padding: "4px 10px",
                    borderRadius: "999px",
                    background: "rgba(255,107,0,.1)",
                    color: "#ff6b00",
                    fontSize: "10px",
                    fontWeight: 800,
                  }}
                >
                  ID: #{user.id ?? "—"}
                </div>
              </div>

              {[
                {
                  title: "Account Information",
                  items: [
                    { label: "🌐 Language", value: user.language },
                    { label: "🗺 State", value: user.state },
                    { label: "🏙 City", value: user.city },
                    { label: "🆔 UPI ID", value: user.upi_id },
                  ],
                },
              ].map((section) => (
                <div key={section.title}>
                  <h3
                    style={{
                      margin: "0 0 10px",
                      fontSize: "11px",
                      fontWeight: 800,
                      letterSpacing: "0.8px",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    {section.title}
                  </h3>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                      marginBottom: "14px",
                    }}
                  >
                    {section.items.map((item) => (
                      <div
                        key={item.label}
                        style={{
                          padding: "11px 12px",
                          borderRadius: "14px",
                          background: "#ffffff",
                          border: "1px solid #e4ebf1",
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            marginBottom: "5px",
                            fontSize: "9.5px",
                            fontWeight: 800,
                            color: "#94a3b8",
                            textTransform: "uppercase",
                          }}
                        >
                          {item.label}
                        </span>

                        <span
                          style={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 800,
                            color: "#0f172a",
                            wordBreak: "break-word",
                          }}
                        >
                          {safeString(item.value, "—")}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <h3
                style={{
                  margin: "0 0 10px",
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Location & Device
              </h3>

              <div
                style={{
                  padding: "12px 13px",
                  borderRadius: "14px",
                  background: "#ffffff",
                  border: "1px solid #e4ebf1",
                  marginBottom: "8px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontSize: "9.5px",
                    fontWeight: 800,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  📍 Live Location
                </span>

                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "#334155",
                    wordBreak: "break-word",
                  }}
                >
                  {safeString(user.location_live, "—")}
                </span>
              </div>

              <div
                style={{
                  padding: "12px 13px",
                  borderRadius: "14px",
                  background: "#ffffff",
                  border: "1px solid #e4ebf1",
                  marginBottom: "14px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontSize: "9.5px",
                    fontWeight: 800,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  📱 Device UUID
                </span>

                <span
                  style={{
                    display: "block",
                    fontSize: "11px",
                    fontFamily:
                      "SF Mono, Menlo, Consolas, monospace",
                    color: "#005CA9",
                    wordBreak: "break-all",
                  }}
                >
                  {safeString(user.device_uuid, "—")}
                </span>
              </div>

              <h3
                style={{
                  margin: "0 0 10px",
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Bank Details
              </h3>

              <div
                style={{
                  padding: "12px 13px",
                  borderRadius: "14px",
                  background: "#ffffff",
                  border: "1px solid #e4ebf1",
                  marginBottom: "14px",
                }}
              >
                <span
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontSize: "9.5px",
                    fontWeight: 800,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  💳 Bank Info
                </span>

                <span
                  style={{
                    display: "block",
                    fontSize: "12px",
                    color: "#334155",
                    wordBreak: "break-word",
                  }}
                >
                  {safeString(user.bank_details, "—")}
                </span>
              </div>

              <h3
                style={{
                  margin: "0 0 10px",
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Account Activity
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    padding: "11px 12px",
                    borderRadius: "14px",
                    background: "#ffffff",
                    border: "1px solid #e4ebf1",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "9.5px",
                      fontWeight: 800,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                    }}
                  >
                    🗓 Joined
                  </span>

                  <span
                    style={{
                      display: "block",
                      fontSize: "11.5px",
                      color: "#334155",
                    }}
                  >
                    {formatDate(user.created_at)}
                  </span>
                </div>

                <div
                  style={{
                    padding: "11px 12px",
                    borderRadius: "14px",
                    background: "#ffffff",
                    border: "1px solid #e4ebf1",
                  }}
                >
                  <span
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "9.5px",
                      fontWeight: 800,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                    }}
                  >
                    🔄 Last Updated
                  </span>

                  <span
                    style={{
                      display: "block",
                      fontSize: "11.5px",
                      color: "#334155",
                    }}
                  >
                    {formatDate(user.updated_at)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            padding: "12px 15px",
            borderTop: "1px solid #e9eef3",
            background: "#f8fafc",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              border: "none",
              borderRadius: "13px",
              padding: "12px",
              background:
                "linear-gradient(135deg,#0066cc,#008cff)",
              color: "#ffffff",
              fontSize: "11px",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
}